import { USER_ACCESS_LEVEL, DQ_PRO_PLAN_KEY } from './constants';

/**
 * Check if a product is enabled for the current user session.
 *
 * @param {object} user - The user object from the session/login response.
 * @param {string} productKey - The product key to check (e.g. 'learning_program').
 * @returns {boolean}
 */
export const isProductEnabled = (user, productKey) => {
    return user?.product_access?.[productKey]?.enabled === true;
};

/**
 * Get the product config for a specific product.
 *
 * @param {object} user - The user object from the session/login response.
 * @param {string} productKey - The product key.
 * @returns {object}
 */
export const getProductConfig = (user, productKey) => {
    return user?.product_access?.[productKey]?.config || {};
};

/**
 * Get all enabled product keys for the current user.
 *
 * @param {object} user - The user object from the session/login response.
 * @returns {string[]}
 */
export const getEnabledProducts = (user) => {
    if (!user?.product_access) return [];
    return Object.entries(user.product_access)
        .filter(([, val]) => val.enabled === true)
        .map(([key]) => key);
};

/**
 * Check if a PAC feature is enabled for the current user session.
 * Reads `user.feature_access` (resolved server-side at login).
 */
export const isFeatureEnabled = (user, featureKey) => {
    return user?.feature_access?.[featureKey]?.enabled === true;
};

/**
 * Get the granular permissions ({view, create, link}) for a product.
 *
 * @param {object} user - The user object from the session/login response.
 * @param {string} productKey - The product key.
 * @returns {object} e.g. {view: true, create: false, link: true} — empty object if none.
 */
export const getProductPermissions = (user, productKey) => {
    return user?.product_access?.[productKey]?.permissions || {};
};

/**
 * Check whether the user holds a specific permission on a product.
 * Requires the product to be enabled AND the permission flag to be true.
 *
 * @param {object} user - The user object from the session/login response.
 * @param {string} productKey - The product key.
 * @param {'view'|'create'|'link'} permission - The permission to check.
 * @returns {boolean}
 */
export const hasProductPermission = (user, productKey, permission) => {
    const access = user?.product_access?.[productKey];
    if (access?.enabled !== true) return false;
    // Enabled product with no granular permissions object => treat as full access
    // (e.g. owner/full-access sessions that don't carry per-permission flags).
    if (access.permissions == null) return true;
    return access.permissions[permission] === true;
};

export const canViewProduct   = (user, productKey) => hasProductPermission(user, productKey, 'view');
export const canCreateProduct = (user, productKey) => hasProductPermission(user, productKey, 'create');
export const canLinkProduct   = (user, productKey) => hasProductPermission(user, productKey, 'link');

/**
 * Check whether the user is the workspace owner (level 20/21, not a peer
 * colleague login). Used to gate write actions on resources — e.g. a learning
 * program — that anyone in the workspace may view but only the owner or its
 * own creator may modify.
 *
 * @param {object} user - The user object from the session/login response.
 * @returns {boolean}
 */
export const isOwner = (user) =>
    (user?.access_level === USER_ACCESS_LEVEL.FULL_ACCESS_DQ_INSIDER_PORTAL
        || user?.access_level === USER_ACCESS_LEVEL.FULL_ACCESS_DQ_INSIDER_PORTAL_SINGLE_INSTITUTE)
    && user?.is_peer !== true;

/**
 * Resolve a user's DQ Pro deployment stage from their enterprise plan key —
 * P0 (baseline assessment only), P1 (learning programs deployed) or LC
 * (LearnCreator / later confirmation stage). Several dashboards gate
 * sections on this same stage, so resolve it here once rather than each
 * page re-deriving it from dq_pro_plan.plan_key on its own.
 *
 * @param {object} user - The user object from the session/login response.
 * @returns {'P0'|'P1'|'LC'} P0 is the baseline default when the plan key is
 *   missing or unrecognized, not an error state.
 */
/**
 * The partner plan whose solution a plan sells: a direct plan (direct_p0/p1b/lc/p2a/p2b)
 * maps to its enterprise plan, every other key to itself. Use it wherever behaviour
 * depends on the SOLUTION (P0 / P1 / LC / P2); keep the real key for channel and
 * ownership questions, and for the variant (P1B vs P1A) — see PLAN_TO_SOLUTIONS.
 */
const DIRECT_TO_ENTERPRISE = {
    [DQ_PRO_PLAN_KEY.DIRECT_P0]: DQ_PRO_PLAN_KEY.ENTERPRISE_P0,
    [DQ_PRO_PLAN_KEY.DIRECT_P1B]: DQ_PRO_PLAN_KEY.ENTERPRISE_P1,
    [DQ_PRO_PLAN_KEY.DIRECT_LC]: DQ_PRO_PLAN_KEY.ENTERPRISE_LC,
    [DQ_PRO_PLAN_KEY.DIRECT_P2A]: DQ_PRO_PLAN_KEY.ENTERPRISE_P2,
    [DQ_PRO_PLAN_KEY.DIRECT_P2B]: DQ_PRO_PLAN_KEY.ENTERPRISE_P2,
    // Legacy keys (see DQ_PRO_PLAN_KEY).
    [DQ_PRO_PLAN_KEY.DIRECT_P1]: DQ_PRO_PLAN_KEY.ENTERPRISE_P1,
    [DQ_PRO_PLAN_KEY.DIRECT_P2]: DQ_PRO_PLAN_KEY.ENTERPRISE_P2,
};
export const solutionPlanKey = (planKey) => DIRECT_TO_ENTERPRISE[planKey] || planKey;

/**
 * Is LINKING free on this workspace's plan?
 *
 * On the PbDQ partner (enterprise_*) and direct (direct_*) plans, linking an
 * assessment or a program down the chain — partner -> network -> school ->
 * class — only makes it available to the level below: it needs no licences and
 * costs no credits (product owner, 6 Oct 2026; learnerAccessIsFree in the API's
 * credit_pool.service.js). A learner's cost is the licence spent when their
 * class is published. Legacy plans still check the balance, as before.
 */
export const isLinkingFreeOnPlan = (user) => {
    const key = String(user?.dq_pro_plan?.plan_key || '');
    return key.startsWith('enterprise') || key.startsWith('direct');
};

export const getPlanStage = (user) => {
    const planKey = solutionPlanKey(user?.dq_pro_plan?.plan_key);
    if (planKey === DQ_PRO_PLAN_KEY.ENTERPRISE_P1) return 'P1';
    if (planKey === DQ_PRO_PLAN_KEY.ENTERPRISE_LC) return 'LC';
    return 'P0';
};

// Shared (frozen) so callers get a stable reference, e.g. as a hook dependency.
const LC_DQC_RANGE = Object.freeze([1, 24]);
const COS_READINESS_DQC_RANGE = Object.freeze([1, 8]);

/**
 * The DQC range ([min, max], inclusive) a solution's programs may target.
 * LearnCreator offers every core competency (DQC01-24) at any age; School COS
 * Readiness (P1, P1A/P1B alike) only DQC01-08; other solutions keep the
 * audience-tier rule.
 *
 * @param {object} user - The user object from the session/login response.
 * @returns {number[]|null} [min, max], or null when the tier rule applies.
 */
export const getSolutionDqcRange = (user) => {
    const planKey = solutionPlanKey(user?.dq_pro_plan?.plan_key);
    if (planKey === DQ_PRO_PLAN_KEY.ENTERPRISE_LC) return LC_DQC_RANGE;
    if ([DQ_PRO_PLAN_KEY.ENTERPRISE_P1, DQ_PRO_PLAN_KEY.ENTERPRISE_P1A, DQ_PRO_PLAN_KEY.ENTERPRISE_P1B].includes(planKey)) {
        return COS_READINESS_DQC_RANGE;
    }
    return null;
};
